import type { Message } from '../types';
import { buildTurnPresentation } from './presentation/buildTurnPresentation';
import { buildDeliverableName } from './presentation/deliverableName';
import type { ArtifactFileStatus } from './presentation/presentationTypes';
import { getActiveRepairVersion, getRepairBaseVersion } from './messageTopic';
import { isInternalStatusExplanation } from './structuredResponse';
import { getWorkspaceMessageCode, getWorkspaceValidationReport, type WorkspaceArtifact } from './workspaceViewModel';

function statusForFile(status: ArtifactFileStatus): WorkspaceArtifact['status'] {
  switch (status) {
    case 'ready':
      return 'passed';
    case 'unvalidated':
    case 'failed':
      return 'failed';
    case 'generating':
    case 'preparing':
      return 'running';
  }
}

function summaryForName(message: Message): string {
  if (typeof message.parsed?.summary === 'string' && message.parsed.summary.trim()) {
    return message.parsed.summary.trim();
  }
  if (typeof message.parsed?.explanation !== 'string') return '';
  if (isInternalStatusExplanation(message.parsed.explanation)) return '';
  return message.parsed.explanation.replace(/```mermaid[\s\S]*?```/gi, '').trim();
}

/**
 * Project the exact files exposed by completed conversation turns into the
 * Engineering Results ledger. This intentionally uses the same presentation
 * builder as the conversation file chip, so the count and filenames cannot
 * silently diverge after reload or when registry ingestion is partial.
 */
export function buildWorkspaceDeliverables(
  messages: Message[],
  language: string,
): WorkspaceArtifact[] {
  const artifacts: WorkspaceArtifact[] = [];

  for (const message of messages) {
    if (message.role !== 'assistant') continue;
    if (!getWorkspaceValidationReport(message) && !message.parsed?.code && !baseCode(message)) continue;
    // The version that shipped, not the one it started from.
    //
    // The content beneath this already resolves to the delivered version —
    // getWorkspaceMessageCode prefers the active repair version — but the name
    // and the date were taken from `base`, which is the code as first generated.
    // So a turn whose repair loop ran twice and whose final round passed CODESYS
    // validation at 14:32 offered its deliverable as `…_V1.st`, stamped 14:19,
    // sitting under a version list whose accepted entry read V1.1. Reported
    // 2026-09-09 as 「为啥交付的不是最后这个版本」 — a reasonable reading of a file
    // that says V1 and carries the time the turn began.
    //
    // Falls back to the base version, which is the only one a turn that never
    // repaired has.
    const deliveredVersion = getActiveRepairVersion(message) || getRepairBaseVersion(message);
    const codeVersionLabel = deliveredVersion?.label || 'V1.0';
    const deliveredAt = deliveredVersion?.createdAt ?? message.timestamp ?? Date.now();
    const summary = summaryForName(message);
    const validation = getWorkspaceValidationReport(message) as Record<string, unknown> | null;
    const deliverableName = buildDeliverableName({
      requirement: message.pipeline?.requirement,
      summary,
      codeVersionLabel,
      createdAt: deliveredAt,
      // The delivered version's own verdict when it carries one: a turn that
      // repaired its way to a pass must not be named after the base's failure.
      validated: deliveredVersion?.validation
        ? deliveredVersion.validation.valid !== false
        : validation?.valid !== false,
      code: getWorkspaceMessageCode(message),
    });
    const presentation = buildTurnPresentation(message, {
      codeVersionLabel,
      deliverableName,
      includeMarkdownArtifacts: true,
      language,
    });

    for (const file of presentation.artifacts) {
      artifacts.push({
        id: file.id,
        kind: 'file',
        title: file.fileName || file.title,
        status: statusForFile(file.status),
        version: file.version,
        codeVersionLabel: file.versionLabel,
        sourceMessageId: file.sourceMessageId || message.id,
        createdAt: deliveredAt,
        origin: 'deliverable',
        fileName: file.fileName,
        content: file.content,
        previewTarget: file.previewTarget,
        downloadable: file.downloadable,
      });
    }
  }

  return artifacts;
}

function baseCode(message: Message): string {
  return getRepairBaseVersion(message)?.code || '';
}
